import React, { useState, useEffect } from "react";
import BlogDataBox from "@/components/blogComponent/blogDataBox";
import { getAllBlogs } from "@/pages/api";
import UrlPagination from "@/components/pagination/UrlPagination";
import { useRouter } from "next/router";
import Head from "next/head";
import { DEFAULT_SEO_VALUES } from "@/commons/constants";

const Blog = ({ initialBlogData, totalBlogs, initialPageSize }) => {
    const router = useRouter();
    // Each page's posts come from getServerSideProps; pagination links do a
    // normal navigation, so props always match the ?page in the URL.
    const blogData = initialBlogData;
    const pageSize = initialPageSize || 8;
    const [isFetching, setIsFetching] = useState(false);

    const totalPages = Math.ceil(totalBlogs / pageSize);
    const currentPage = Number(router.query.page || 1);

    useEffect(() => {
        const start = (url) => url.split("?")[0] === "/vidya" && setIsFetching(true);
        const done = () => setIsFetching(false);
        router.events.on("routeChangeStart", start);
        router.events.on("routeChangeComplete", done);
        router.events.on("routeChangeError", done);
        return () => {
            router.events.off("routeChangeStart", start);
            router.events.off("routeChangeComplete", done);
            router.events.off("routeChangeError", done);
        };
    }, [router.events]);

    const pageSuffix = currentPage > 1 ? ` - Page ${currentPage}` : "";
    const seoTitle = `Vidya: Spiritual Insights, Puja Vidhi & Festival Guides${pageSuffix} | BookMyPooja`;
    const seoDescription = "Read BookMyPooja's Vidya articles on puja vidhi, festival dates, vrat kathas, rituals and Hindu traditions, written to help you perform every ceremony the right way.";
    const canonical = `${process.env.NEXT_PUBLIC_SITE_URL}/vidya${currentPage > 1 ? `?page=${currentPage}` : ""}`;

    return (
      <>
      <Head>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={DEFAULT_SEO_VALUES.imageLink} />
      </Head>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
          {/* Header Section */}
          <div className="bg-white shadow-sm border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <div className="text-center">
                      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                          Spiritual Insights & Blogs
                      </h1>
                      <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                          Discover ancient wisdom, festive traditions, and spiritual guidance for modern life
                      </p>

                      {/* Blog count and page info */}
                      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                {totalBlogs} Articles Available
                            </span>
                          {totalPages > 1 && (
                            <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"/>
                                    </svg>
                                    Page {currentPage} of {totalPages}
                                </span>
                          )}
                      </div>
                  </div>
              </div>
          </div>

          {/* Main Content Area */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

              {/* Loading State */}
              {isFetching && (
                <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
                        <p className="mt-4 text-gray-600 font-medium">Loading articles...</p>
                    </div>
                </div>
              )}

              {/* Blog Grid */}
              <div className={`transition-all duration-500 ${isFetching ? "opacity-50" : "opacity-100"}`}>
                  {blogData && blogData.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 mb-12">
                        {blogData.map((data, index) => (
                          <div
                            key={`${data.blogSlug?.current}-${currentPage}-${index}`}
                            className="transform hover:scale-105 transition-transform duration-300"
                          >
                              <BlogDataBox
                                mainImageSrc={data.blogHeadingImageUrl}
                                slug={data.blogSlug?.current}
                                mainImageAlt={data.blogTitle}
                                title={data.blogTitle}
                                description={data.blogShortSummary}
                                author={data.blogAuthor}
                                date={data.blogPublishedOn}
                                icon1Src={data.icon1Src}
                                icon1Alt={data.icon1Alt}
                                icon2Src={data.icon2Src}
                                icon2Alt={data.icon2Alt}
                              />
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                        <div className="max-w-md mx-auto">
                            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                            </svg>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Articles Found</h3>
                            <p className="text-gray-600">Check back later for new spiritual insights and blog posts.</p>
                        </div>
                    </div>
                  )}
              </div>

              {/* Pagination Section - Always visible when there are multiple pages */}
              {totalPages > 1 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-gray-600">
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalBlogs)} of {totalBlogs} articles
                        </div>
                        <UrlPagination nPages={totalPages} currentPage={currentPage}/>
                    </div>
                </div>
              )}
          </div>
      </div>
      </>
    );
};

export async function getServerSideProps(context) {
    const pageSize = 8;
    const page = Number(context.query.page || 1);

    try {
        const res = await getAllBlogs(page, pageSize, "all");
        if (res && res.status === 200 && res.data && res.data.statusCode === 200 && res.data.body) {
            const { body: blogData, totalCount: total } = res.data;
            return {
                props: { initialBlogData: blogData, totalBlogs: total, initialPageSize: pageSize },
            };
        }
    } catch (error) {
        console.error("Error fetching initial blog data:", error.message);
    }

    return {
        props: { initialBlogData: [], totalBlogs: 0, initialPageSize: pageSize },
    };
}

export default Blog;